import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Github, Linkedin, Mail, ExternalLink } from 'lucide-react';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 200) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const socialLinks = [
    { icon: <Github className="w-5 h-5" />, url: 'https://github.com/hugowrtma', label: 'GitHub', color: 'hover:text-gray-300' },
    { icon: <Linkedin className="w-5 h-5" />, url: 'https://linkedin.com/in/hugowiratama', label: 'LinkedIn', color: 'hover:text-blue-400' },
    { icon: <Mail className="w-5 h-5" />, url: 'mailto:hugowiratama21@gmail.com', label: 'Email', color: 'hover:text-green-400' },
    { icon: <ExternalLink className="w-5 h-5" />, url: 'https://linkhub-hugo.vercel.app', label: 'All Links', color: 'hover:text-purple-400' }
  ];

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Projects', href: '#recent-works' },
    { name: 'Experience', href: '#experiences' },
    { name: 'Contact', href: '#contact' }
  ];

  return (
    <footer className="bg-gradient-to-t from-gray-900 via-slate-900 to-gray-900 text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500 rounded-full blur-2xl" />
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-500 rounded-full blur-2xl" />
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="space-y-4">
            <h5 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Open to new opportunities
            </h5>
            <p className="text-gray-400 leading-relaxed max-w-sm">
              Ready to contribute, learn, and grow with a dynamic team.
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Made with</span>
              <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                <Heart className="w-4 h-4 text-red-500 fill-current" />
              </motion.div>
              <span>using React & Tailwind CSS</span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="space-y-4">
            <h4 className="text-lg font-semibold text-blue-400 mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <motion.a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    whileHover={{ x: 5 }}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer inline-block"
                  >
                    {link.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Connect Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-4">
            <h4 className="text-lg font-semibold text-purple-400 mb-6">Let's Connect</h4>
            <p className="text-gray-400 text-sm mb-6">
              Follow my journey and let's build something amazing together.
            </p>
            <div className="flex gap-4">
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={
                  social.label === "All Links"
                    ? {} // gak ada scale pas hover
                    : { scale: 1.1, y: -2 } // normal link tetap scale
                }
                whileTap={
                  social.label === "All Links"
                    ? {} // gak ada tap effect juga
                    : { scale: 0.95 }
                }
                animate={
                  social.label === "All Links"
                    ? { y: [0, -17, 0], scale: [1, 1, 1] } // scale selalu 1
                    : {}
                }
                transition={
                  social.label === "All Links"
                    ? { duration: 1, repeat: Infinity, ease: "linear" }
                    : {}
                }
                className={`p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg text-gray-400 ${social.color}`}
                aria-label={social.label}
              >
                {social.icon}
              </motion.a>
            ))}
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700/50 pt-8 mt-12">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="text-gray-500 text-sm text-center md:text-center">
              © {new Date().getFullYear()} Self Portofolio. All rights reserved.
            </motion.p>
          </div>
        </div>

        {/* Back to Top */}
        <AnimatePresence>
          {isVisible && (
            <motion.button
              key="backToTop"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              whileHover={{ scale: 1.1, y: -4 }}
              whileTap={{ scale: 0.95 }}
              className="fixed bottom-8 right-8 p-3 
                        bg-white/20 backdrop-blur-sm 
                        border border-white/20 
                        rounded-full text-white 
                        hover:bg-white/30 
                        shadow-lg shadow-white/10 z-50"
              aria-label="Back to top"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </footer>
  );
};

export default Footer;